import styled from '@emotion/styled';
import type { ComponentProps } from 'react';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { tokens, Modal, Tag } from '@hotjar/hotsauce';

const Grid = styled.div`
  overflow: auto;
  background: linear-gradient(${tokens.color.neutral_100}, ${tokens.color.neutral_100}) center/1px
    100% no-repeat;
  display: grid;
  grid-gap: ${tokens.spacer.x4};
  grid-template-columns: 1fr 1fr;

  padding: 0 ${tokens.spacer.x2} ${tokens.spacer.x2} ${tokens.spacer.x2};
`;

const KeystrokeTag = styled(Tag)`
  font-size: 12px;
  height: ${tokens.spacer.x3};
`;

const ShortcutRow = styled.div`
  align-items: center;
  display: flex;
  font-size: 12px;
  justify-content: space-between;
  padding: ${tokens.spacer.x2} 0;
  > span {
    white-space: nowrap;
  }

  &:not(:last-child) {
    border-bottom: 1px solid ${tokens.color.ui_border_light};
  }
`;

interface ModalProps {
  isOpen: ComponentProps<typeof Modal>['isOpen'];
  onClose: ComponentProps<typeof Modal>['onClose'];
  isNewHighlightsOn: boolean;
  isReadOnly: boolean;
  isTokenShared: boolean;
  isPlaying: boolean;
  onPlay: (showAnimation?: boolean) => void;
  onPause: (showAnimation?: boolean, isManual?: boolean) => void;
}

export const ShortcutsLegendModal = ({
  isOpen,
  onClose,
  isNewHighlightsOn,
  isReadOnly,
  isTokenShared,
  isPlaying,
  onPause,
  onPlay,
}: ModalProps): JSX.Element => {
  const { t } = useTranslation();
  const [wasPlaying, setWasPlaying] = useState(false);

  const pauseRecordingIfNeeded = useCallback(() => {
    setWasPlaying(isPlaying);
    if (isPlaying) {
      onPause();
    }
  }, [isPlaying, onPause]);
  const resetRecordingPlayState = useCallback(() => {
    if (wasPlaying) {
      onPlay();
    }
  }, [wasPlaying, onPlay]);

  return (
    <Modal
      appElementSelector="body"
      contentLabel={t('common:player.keyboard-shortcuts-modal.title')}
      isOpen={isOpen}
      onClose={onClose}
      className="keyboard-shortcut-modal"
      onAfterOpen={pauseRecordingIfNeeded}
      onAfterClose={resetRecordingPlayState}
      shouldCloseOnEsc
      shouldCloseOnOverlayClick
    >
      <Modal.Header title="Keyboard shortcuts" />
      <Grid>
        <div>
          <ShortcutRow>
            {t('common:player.shortcuts.play-or-pause')}
            <span>
              <KeystrokeTag>k</KeystrokeTag>&nbsp;or&nbsp;
              <KeystrokeTag>space</KeystrokeTag>
            </span>
          </ShortcutRow>
          <ShortcutRow>
            {t('common:player.shortcuts.toggle-speed')}
            <KeystrokeTag>i</KeystrokeTag>
          </ShortcutRow>
          <ShortcutRow>
            {t('common:player.shortcuts.rewind-x-seconds')}
            <span>
              <KeystrokeTag>left arrow</KeystrokeTag>
            </span>
          </ShortcutRow>
          <ShortcutRow>
            {t('common:player.shortcuts.skip-x-seconds')}
            <span>
              <KeystrokeTag>right arrow</KeystrokeTag>
            </span>
          </ShortcutRow>
          <ShortcutRow>
            {t('common:player.shortcuts.jump-to-prev-page')}
            <span>
              <KeystrokeTag>shift</KeystrokeTag>&nbsp;+&nbsp;
              <KeystrokeTag>left arrow</KeystrokeTag>
            </span>
          </ShortcutRow>
          <ShortcutRow>
            {t('common:player.shortcuts.jump-to-next-page')}
            <span>
              <KeystrokeTag>shift</KeystrokeTag>&nbsp;+&nbsp;
              <KeystrokeTag>right arrow</KeystrokeTag>
            </span>
          </ShortcutRow>
          {!isTokenShared && (
            <>
              <ShortcutRow>
                {t('common:player.shortcuts.load-prev-recording')}
                <span>
                  <KeystrokeTag>shift</KeystrokeTag>&nbsp;+&nbsp;
                  <KeystrokeTag>p</KeystrokeTag>
                </span>
              </ShortcutRow>
              <ShortcutRow>
                {t('common:player.shortcuts.load-next-recording')}
                <span>
                  <KeystrokeTag>shift</KeystrokeTag>&nbsp;+&nbsp;
                  <KeystrokeTag>n</KeystrokeTag>
                </span>
              </ShortcutRow>
            </>
          )}
        </div>
        <div>
          <ShortcutRow>
            {t('common:player.shortcuts.skip-current-inactivity')}
            <KeystrokeTag>s</KeystrokeTag>
          </ShortcutRow>

          <ShortcutRow>
            {t('common:player.replay')}
            <KeystrokeTag>r</KeystrokeTag>
          </ShortcutRow>
          {(!isTokenShared || !isReadOnly) && (
            <ShortcutRow>
              {t('common:player.shortcuts.delete')}
              <KeystrokeTag>d</KeystrokeTag>
            </ShortcutRow>
          )}
          {!isTokenShared && (
            <ShortcutRow>
              {isNewHighlightsOn
                ? t('common:player.shortcuts.save-clip-as-highlight')
                : t('common:player.shortcuts.add-comment')}
              <KeystrokeTag>c</KeystrokeTag>
            </ShortcutRow>
          )}
          <ShortcutRow>
            {t('common:player.shortcuts.collapse-expand-sidebar')}
            <KeystrokeTag>h</KeystrokeTag>
          </ShortcutRow>
          <ShortcutRow>
            {t('common:player.shortcuts.show-modal')}
            <KeystrokeTag>?</KeystrokeTag>
          </ShortcutRow>
        </div>
      </Grid>
    </Modal>
  );
};
